import {
  CategoryScale,
  Chart as ChartJS,
  Filler,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
} from 'chart.js';
import { motion } from 'framer-motion';
import {
  BarChart3,
  Calculator,
  Calendar,
  FileText,
  IndianRupee,
  TrendingDown,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { Line } from 'react-chartjs-2';

import { ToolLayout } from '@layouts/ToolLayout';

import { LocalizedCurrency } from '@ui/LocalizedNumber';
import NumberInputWithSlider from '@ui/NumberInputWithSlider';
import RadioGroup from '@ui/RadioGroup';
import { ResultSummaryCard } from '@ui/ResultSummaryCard';
import Toggle from '@ui/Toggle';
import { WhatsAppCTA } from '@ui/WhatsAppCTA';

import { COLORS } from '@constants/colors';
import { CONTACT_INFO } from '@constants/contact';

import {
  formatCurrency,
  formatIndianNumberShort,
} from '@utils/numberFormatter';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

export const SWPCalculator = () => {
  // SWP Calculator State
  const [initialInvestment, setInitialInvestment] = useState<number | null>(
    1000000
  );
  const [monthlyWithdrawal, setMonthlyWithdrawal] = useState<number | null>(
    8000
  );
  const [expectedReturn, setExpectedReturn] = useState<number | null>(12);
  const [withdrawalPeriod, setWithdrawalPeriod] = useState<number | null>(15);
  const [stepUpEnabled, setStepUpEnabled] = useState<boolean>(false);
  const [stepUpType, setStepUpType] = useState<'percentage' | 'absolute'>(
    'percentage'
  );
  const [stepUpValue, setStepUpValue] = useState<number | null>(5);
  const [results, setResults] = useState({
    totalWithdrawals: 0,
    remainingCorpus: 0,
    totalGrowth: 0,
    monthsToExhaustion: 0,
    isCorpusExhausted: false,
  });

  // Calculate SWP returns with step-up
  const calculateSWP = () => {
    // Handle null values by using defaults
    const initialAmount = initialInvestment || 0;
    const monthlyWithdraw = monthlyWithdrawal || 0;
    const returnRate = expectedReturn || 0;
    const period = withdrawalPeriod || 0;
    const stepUp = stepUpValue || 0;

    const monthlyRate = returnRate / 100 / 12;
    let currentCorpus = initialAmount;
    let totalWithdrawn = 0;
    let currentMonthlyWithdrawal = monthlyWithdraw;
    let monthsToExhaustion = 0;
    let isExhausted = false;

    // Calculate month by month
    for (let month = 1; month <= period * 12; month++) {
      // Check if we can make the withdrawal (at beginning of month)
      if (currentCorpus >= currentMonthlyWithdrawal) {
        currentCorpus -= currentMonthlyWithdrawal;
        totalWithdrawn += currentMonthlyWithdrawal;
      } else {
        // Corpus exhausted
        totalWithdrawn += currentCorpus;
        currentCorpus = 0;
        monthsToExhaustion = month;
        isExhausted = true;
        break;
      }

      // Apply growth to remaining corpus (after withdrawal)
      currentCorpus = currentCorpus * (1 + monthlyRate);

      // Apply step-up annually
      if (stepUpEnabled && stepUp > 0 && month % 12 === 0) {
        if (stepUpType === 'percentage') {
          currentMonthlyWithdrawal =
            currentMonthlyWithdrawal * (1 + stepUp / 100);
        } else {
          currentMonthlyWithdrawal = currentMonthlyWithdrawal + stepUp;
        }
      }
    }

    const totalGrowth = totalWithdrawn + currentCorpus - initialAmount;

    setResults({
      totalWithdrawals: totalWithdrawn,
      remainingCorpus: currentCorpus,
      totalGrowth,
      monthsToExhaustion,
      isCorpusExhausted: isExhausted,
    });
  };

  // Generate chart data for corpus depletion over time
  const generateChartData = () => {
    // Handle null values by using defaults
    const initialAmount = initialInvestment || 0;
    const monthlyWithdraw = monthlyWithdrawal || 0;
    const returnRate = expectedReturn || 0;
    const period = withdrawalPeriod || 0;
    const stepUp = stepUpValue || 0;

    const years = [];
    const corpusData = [];
    const yearlyWithdrawalsData = [];

    const monthlyRate = returnRate / 100 / 12;

    // Add starting point
    years.push('Today');
    corpusData.push(initialAmount);
    yearlyWithdrawalsData.push(0);

    let currentCorpus = initialAmount;
    let currentMonthlyWithdrawal = monthlyWithdraw;

    for (let year = 1; year <= period; year++) {
      let yearlyWithdrawalAmount = 0;

      // Calculate year by year
      for (let month = 1; month <= 12; month++) {
        // Make withdrawal if possible (at beginning of month)
        if (currentCorpus >= currentMonthlyWithdrawal) {
          currentCorpus -= currentMonthlyWithdrawal;
          yearlyWithdrawalAmount += currentMonthlyWithdrawal;
        } else {
          // Corpus exhausted
          yearlyWithdrawalAmount += currentCorpus;
          currentCorpus = 0;
          break;
        }

        // Apply growth to remaining corpus
        currentCorpus = currentCorpus * (1 + monthlyRate);

        // Apply step-up annually
        if (stepUpEnabled && stepUp > 0 && month === 12) {
          if (stepUpType === 'percentage') {
            currentMonthlyWithdrawal =
              currentMonthlyWithdrawal * (1 + stepUp / 100);
          } else {
            currentMonthlyWithdrawal = currentMonthlyWithdrawal + stepUp;
          }
        }
      }

      years.push(`Year ${year}`);
      corpusData.push(Math.max(0, currentCorpus));
      yearlyWithdrawalsData.push(yearlyWithdrawalAmount);

      // Break if corpus is exhausted
      if (currentCorpus <= 0) break;
    }

    return {
      labels: years,
      datasets: [
        {
          label: 'Remaining corpus',
          data: corpusData,
          borderColor: COLORS.accent,
          backgroundColor: `${COLORS.accent}20`,
          fill: true,
          tension: 0.4,
          pointBackgroundColor: COLORS.accent,
          pointBorderColor: COLORS.accent,
          pointRadius: 4,
          pointHoverRadius: 6,
        },
        {
          label: 'Yearly withdrawal',
          data: yearlyWithdrawalsData,
          borderColor: COLORS.sage,
          backgroundColor: `${COLORS.sage}20`,
          fill: false,
          tension: 0.4,
          pointBackgroundColor: COLORS.sage,
          pointBorderColor: COLORS.sage,
          pointRadius: 4,
          pointHoverRadius: 6,
        },
      ],
    };
  };

  const generateTableData = () => {
    // Handle null values by using defaults
    const initialAmount = initialInvestment || 0;
    const monthlyWithdraw = monthlyWithdrawal || 0;
    const returnRate = expectedReturn || 0;
    const period = withdrawalPeriod || 0;
    const stepUp = stepUpValue || 0;

    const tableData = [];
    const monthlyRate = returnRate / 100 / 12;

    // Add initial row
    tableData.push({
      year: 'Today',
      isInitial: true,
      monthlyWithdrawal: 0,
      yearlyWithdrawal: 0,
      cumulativeWithdrawals: 0,
      corpusAtStart: initialAmount,
      corpusAtEnd: initialAmount,
      growth: 0,
    });

    let currentCorpus = initialAmount;
    let cumulativeWithdrawals = 0;
    let currentMonthlyWithdrawal = monthlyWithdraw;

    for (let year = 1; year <= period; year++) {
      const corpusAtYearStart = currentCorpus;
      let yearlyWithdrawalAmount = 0;

      // Calculate year by year
      for (let month = 1; month <= 12; month++) {
        // Make withdrawal if possible (at beginning of month)
        if (currentCorpus >= currentMonthlyWithdrawal) {
          currentCorpus -= currentMonthlyWithdrawal;
          yearlyWithdrawalAmount += currentMonthlyWithdrawal;
          cumulativeWithdrawals += currentMonthlyWithdrawal;
        } else {
          // Corpus exhausted
          yearlyWithdrawalAmount += currentCorpus;
          cumulativeWithdrawals += currentCorpus;
          currentCorpus = 0;
          break;
        }

        // Apply growth to remaining corpus
        currentCorpus = currentCorpus * (1 + monthlyRate);

        // Apply step-up annually
        if (stepUpEnabled && stepUp > 0 && month === 12) {
          if (stepUpType === 'percentage') {
            currentMonthlyWithdrawal =
              currentMonthlyWithdrawal * (1 + stepUp / 100);
          } else {
            currentMonthlyWithdrawal = currentMonthlyWithdrawal + stepUp;
          }
        }
      }

      const growth = currentCorpus + yearlyWithdrawalAmount - corpusAtYearStart;

      tableData.push({
        year,
        isInitial: false,
        monthlyWithdrawal:
          stepUpEnabled && stepUp > 0
            ? year === 1
              ? monthlyWithdraw
              : stepUpType === 'percentage'
                ? monthlyWithdraw * Math.pow(1 + stepUp / 100, year - 1)
                : monthlyWithdraw + stepUp * (year - 1)
            : monthlyWithdraw,
        yearlyWithdrawal: yearlyWithdrawalAmount,
        cumulativeWithdrawals,
        corpusAtStart: corpusAtYearStart,
        corpusAtEnd: currentCorpus,
        growth,
      });

      // Break if corpus is exhausted
      if (currentCorpus <= 0) break;
    }

    return tableData;
  };

  // Chart options
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top' as const,
        labels: {
          usePointStyle: true,
          padding: 20,
          font: {
            size: 12,
            weight: 'bold' as const,
          },
        },
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: '#fff',
        bodyColor: '#fff',
        borderColor: COLORS.accent,
        borderWidth: 1,
        cornerRadius: 8,
        displayColors: true,
        callbacks: {
          label: function (context: any) {
            const value = `₹${context.parsed.y.toLocaleString('en-IN')}`;
            return `${context.dataset.label}: ${value}`;
          },
        },
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
        ticks: {
          font: {
            size: 11,
          },
          color: COLORS.smoke,
        },
      },
      y: {
        grid: {
          color: `${COLORS.smoke}20`,
        },
        ticks: {
          font: {
            size: 11,
          },
          color: COLORS.charcoal,
          callback: function (value: any) {
            return `₹${value.toLocaleString('en-IN')}`;
          },
        },
        title: {
          display: true,
          text: 'Amount (₹)',
          color: COLORS.charcoal,
          font: {
            size: 12,
            weight: 'bold' as const,
          },
        },
      },
    },
    interaction: {
      intersect: false,
      mode: 'index' as const,
    },
  };

  // Recalculate when inputs change
  useEffect(() => {
    calculateSWP();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    initialInvestment,
    monthlyWithdrawal,
    expectedReturn,
    withdrawalPeriod,
    stepUpEnabled,
    stepUpType,
    stepUpValue,
  ]);

  return (
    <ToolLayout
      title={`SWP calculator | ${CONTACT_INFO.company.name}`}
      heroTitle="SWP calculator"
      description="Calculate your Systematic Withdrawal Plan (SWP) and see how long your corpus will last with regular withdrawals"
      canonicalUrl="https://niveshright.com/tools/swp-calculator"
      backgroundColor={COLORS.mist}
      heroStyle="gradient"
      heroIcon={
        <div
          className="w-16 h-16 rounded-2xl flex items-center justify-center"
          style={{ backgroundColor: `${COLORS.gold}20` }}
        >
          <Calculator size={32} style={{ color: COLORS.gold }} />
        </div>
      }
      showBreadcrumb={true}
      keywords="SWP calculator, systematic withdrawal plan, systematic withdrawal plan calculator, retirement income, corpus depletion, withdrawal calculator, pension planning"
      cta={
        <WhatsAppCTA
          title="Ready to plan your retirement income?"
          description="Get personalized advice on systematic withdrawal planning and retirement income strategies."
          phoneNumber={CONTACT_INFO.phone.whatsapp}
          message={`Hi ${CONTACT_INFO.contact_person.short_name}! I used your SWP calculator with these details:

Initial corpus: ${formatCurrency(initialInvestment || 0)}
Monthly withdrawal: ${formatCurrency(monthlyWithdrawal || 0)}${
            stepUpEnabled && stepUpValue
              ? `
Inflation adjustment: ${stepUpValue}${stepUpType === 'percentage' ? '% annually' : ' (fixed) annually'}`
              : ''
          }
Withdrawal period: ${withdrawalPeriod} years
Expected return: ${expectedReturn || 0}% p.a.

CALCULATION RESULTS:
• Total withdrawals: ${formatCurrency(results.totalWithdrawals)}
• Remaining corpus: ${formatCurrency(results.remainingCorpus)}${
            results.isCorpusExhausted
              ? `
ALERT: Corpus will be exhausted in ${Math.floor(results.monthsToExhaustion / 12)} years ${results.monthsToExhaustion % 12} months`
              : ''
          }
• Net growth: ${formatCurrency(results.totalGrowth)} ${results.totalGrowth >= 0 ? '(Wealth created)' : '(Wealth consumed)'}

I need help planning my retirement income strategy. Can you suggest better withdrawal plans or investment options?`}
          buttonText="Get retirement planning help"
          backgroundColor={COLORS.pearl}
          buttonColor={COLORS.accent}
        />
      }
    >
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
        <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
          <h2
            className="text-2xl font-bold mb-8"
            style={{ color: COLORS.primary }}
          >
            Withdrawal details
          </h2>

          <NumberInputWithSlider
            label="Initial corpus amount"
            value={initialInvestment}
            onChange={setInitialInvestment}
            min={100000}
            max={1000000000}
            step={50000}
            placeholder="1000000"
            prefix="₹"
            showTooltip={true}
            minLabel={`₹${formatIndianNumberShort(100000, false)}`}
            maxLabel={`₹${formatIndianNumberShort(1000000000, false)}`}
            className="mb-8"
          />

          <NumberInputWithSlider
            label="Monthly withdrawal amount"
            value={monthlyWithdrawal}
            onChange={setMonthlyWithdrawal}
            min={1000}
            max={10000000}
            step={500}
            placeholder="8000"
            prefix="₹"
            showTooltip={true}
            minLabel={`₹${formatIndianNumberShort(1000, false)}`}
            maxLabel={`₹${formatIndianNumberShort(10000000, false)}`}
            className="mb-8"
          />

          <NumberInputWithSlider
            label="Expected annual return (%)"
            value={expectedReturn}
            onChange={setExpectedReturn}
            min={1}
            max={100}
            step={0.5}
            placeholder="12"
            minLabel="1%"
            maxLabel="100%"
            className="mb-8"
          />

          <NumberInputWithSlider
            label="Withdrawal period (years)"
            value={withdrawalPeriod}
            onChange={setWithdrawalPeriod}
            min={1}
            max={100}
            step={1}
            placeholder="15"
            minLabel="1 year"
            maxLabel="100 years"
            className="mb-8"
          />

          <Toggle
            label="Enable inflation-adjusted withdrawal"
            checked={stepUpEnabled}
            onChange={setStepUpEnabled}
            className="mb-6"
          />

          {stepUpEnabled && (
            <div className="space-y-6 p-6 bg-gray-50 rounded-xl mb-8">
              {/* Inflation Adjustment Type */}
              <RadioGroup
                label="Inflation adjustment type"
                options={[
                  {
                    value: 'percentage',
                    label: 'Percentage (%)',
                    description:
                      'Increase by % annually (e.g., 6% for inflation)',
                  },
                  {
                    value: 'absolute',
                    label: 'Fixed amount (₹)',
                    description: 'Increase by fixed ₹ annually',
                  },
                ]}
                value={stepUpType}
                onChange={value =>
                  setStepUpType(value as 'percentage' | 'absolute')
                }
                className="mb-0"
              />

              {/* Inflation Value */}
              <NumberInputWithSlider
                label={
                  stepUpType === 'percentage'
                    ? 'Expected inflation rate (%)'
                    : 'Annual increase amount'
                }
                value={stepUpValue}
                onChange={setStepUpValue}
                min={0}
                max={stepUpType === 'percentage' ? 100 : 10000000}
                step={stepUpType === 'percentage' ? 0.5 : 100}
                placeholder={stepUpType === 'percentage' ? '6' : '0'}
                prefix={stepUpType === 'absolute' ? '₹' : undefined}
                showTooltip={stepUpType === 'absolute'}
                minLabel={stepUpType === 'percentage' ? '0%' : '₹0'}
                maxLabel={
                  stepUpType === 'percentage'
                    ? '100%'
                    : `₹${formatIndianNumberShort(10000000, false)}`
                }
                className="mb-0"
              />
            </div>
          )}
        </div>

        {/* Results Section */}
        <div className="space-y-6">
          {/* Summary Cards */}
          <div className="grid grid-cols-1 gap-6">
            <ResultSummaryCard
              title="Total withdrawals"
              value={<LocalizedCurrency value={results.totalWithdrawals} />}
              subtitle={`Money withdrawn over ${withdrawalPeriod} years`}
              icon={TrendingDown}
              iconColor={COLORS.accent}
              valueColor={COLORS.accent}
              delay={0.6}
            />

            <ResultSummaryCard
              title="Remaining corpus"
              value={<LocalizedCurrency value={results.remainingCorpus} />}
              subtitle={
                results.isCorpusExhausted
                  ? `Exhausted in ${Math.floor(results.monthsToExhaustion / 12)} years ${results.monthsToExhaustion % 12} months`
                  : 'Corpus remaining after period'
              }
              icon={IndianRupee}
              iconColor={results.isCorpusExhausted ? '#ef4444' : COLORS.sage}
              valueColor={results.isCorpusExhausted ? '#ef4444' : COLORS.sage}
              delay={0.7}
            />

            <ResultSummaryCard
              title="Total growth"
              value={<LocalizedCurrency value={results.totalGrowth} />}
              subtitle={`(Withdrawals + Remaining) - Initial: (${formatCurrency(results.totalWithdrawals)} + ${formatCurrency(results.remainingCorpus)}) - ${formatCurrency(initialInvestment || 0)}`}
              icon={Calendar}
              iconColor={results.totalGrowth >= 0 ? COLORS.gold : '#ef4444'}
              valueColor={results.totalGrowth >= 0 ? COLORS.gold : '#ef4444'}
              delay={0.8}
            />
          </div>

          {/* Growth Chart */}
          <motion.div
            className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2, delay: 0.2 }}
          >
            <div className="flex items-center mb-6">
              <BarChart3
                size={24}
                style={{ color: COLORS.gold }}
                className="mr-3"
              />
              <h3
                className="text-lg font-semibold"
                style={{ color: COLORS.primary }}
              >
                Corpus and withdrawal trend
              </h3>
            </div>

            <div className="h-80">
              <Line data={generateChartData()} options={chartOptions} />
            </div>

            <div className="mt-6 grid grid-cols-2 gap-4 text-center">
              <div className="p-3 bg-blue-50 rounded-xl">
                <p className="text-sm font-medium text-gray-600">
                  Initial corpus
                </p>
                <p
                  className="text-lg font-bold"
                  style={{ color: COLORS.accent }}
                >
                  <LocalizedCurrency value={initialInvestment || 0} />
                </p>
              </div>
              <div className="p-3 bg-green-50 rounded-xl">
                <p className="text-sm font-medium text-gray-600">
                  Final corpus
                </p>
                <p
                  className="text-lg font-bold"
                  style={{
                    color:
                      results.remainingCorpus > 0 ? COLORS.sage : '#ef4444',
                  }}
                >
                  <LocalizedCurrency value={results.remainingCorpus} />
                </p>
              </div>
            </div>

            <div className="mt-4 p-4 bg-gradient-to-r from-blue-50 to-green-50 rounded-xl text-center">
              <p className="text-sm font-medium text-gray-600 mb-1">
                Total withdrawals
              </p>
              <p
                className="text-2xl font-bold"
                style={{ color: COLORS.accent }}
              >
                <LocalizedCurrency value={results.totalWithdrawals} />
              </p>
              {results.isCorpusExhausted && (
                <p className="text-sm text-red-500 mt-1">
                  ⚠️ Corpus will be exhausted in{' '}
                  {Math.floor(results.monthsToExhaustion / 12)} years{' '}
                  {results.monthsToExhaustion % 12} months
                </p>
              )}
            </div>
          </motion.div>
        </div>
      </div>

      {/* Detailed Withdrawal Table - Full Width */}
      <motion.div
        className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100 mt-8"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 1.0 }}
      >
        <div className="flex items-center mb-6">
          <FileText size={24} style={{ color: COLORS.gold }} className="mr-3" />
          <h3
            className="text-lg font-semibold"
            style={{ color: COLORS.primary }}
          >
            Year-wise withdrawal breakdown
          </h3>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b-2 border-gray-200">
                <th
                  className="text-left py-3 px-2 font-semibold"
                  style={{ color: COLORS.charcoal }}
                >
                  Period
                </th>
                {stepUpEnabled && (stepUpValue || 0) > 0 && (
                  <th
                    className="text-right py-3 px-2 font-semibold"
                    style={{ color: COLORS.charcoal }}
                  >
                    Monthly withdrawal
                  </th>
                )}
                <th
                  className="text-right py-3 px-2 font-semibold"
                  style={{ color: COLORS.charcoal }}
                >
                  Yearly withdrawal
                </th>
                <th
                  className="text-right py-3 px-2 font-semibold"
                  style={{ color: COLORS.charcoal }}
                >
                  Total withdrawn
                </th>
                <th
                  className="text-right py-3 px-2 font-semibold"
                  style={{ color: COLORS.charcoal }}
                >
                  Corpus at start
                </th>
                <th
                  className="text-right py-3 px-2 font-semibold"
                  style={{ color: COLORS.charcoal }}
                >
                  Corpus at end
                </th>
                <th
                  className="text-right py-3 px-2 font-semibold"
                  style={{ color: COLORS.charcoal }}
                >
                  Growth
                </th>
              </tr>
            </thead>
            <tbody>
              {generateTableData().map((row, index) => (
                <tr
                  key={`${row.year}-${index}`}
                  className={`border-b border-gray-100 ${index % 2 === 0 ? 'bg-gray-50' : 'bg-white'} hover:bg-blue-50 transition-colors duration-200 ${row.isInitial ? 'bg-blue-50 font-medium' : ''}`}
                >
                  <td
                    className="py-3 px-2 font-medium"
                    style={{ color: COLORS.primary }}
                  >
                    {row.isInitial ? 'Today (start)' : `Year ${row.year}`}
                  </td>
                  {stepUpEnabled && (stepUpValue || 0) > 0 && (
                    <td className="py-3 px-2 text-right text-gray-700">
                      {row.isInitial ? (
                        '-'
                      ) : (
                        <LocalizedCurrency value={row.monthlyWithdrawal} />
                      )}
                    </td>
                  )}
                  <td className="py-3 px-2 text-right text-gray-700">
                    {row.isInitial ? (
                      '-'
                    ) : (
                      <LocalizedCurrency value={row.yearlyWithdrawal} />
                    )}
                  </td>
                  <td className="py-3 px-2 text-right text-gray-700">
                    <LocalizedCurrency value={row.cumulativeWithdrawals} />
                  </td>
                  <td
                    className="py-3 px-2 text-right font-medium"
                    style={{ color: COLORS.accent }}
                  >
                    <LocalizedCurrency value={row.corpusAtStart} />
                  </td>
                  <td
                    className="py-3 px-2 text-right font-medium"
                    style={{
                      color: row.corpusAtEnd > 0 ? COLORS.sage : '#ef4444',
                    }}
                  >
                    <LocalizedCurrency value={row.corpusAtEnd} />
                  </td>
                  <td
                    className="py-3 px-2 text-right font-medium"
                    style={{
                      color: row.growth >= 0 ? COLORS.sage : '#ef4444',
                    }}
                  >
                    <LocalizedCurrency value={row.growth} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="mt-6 p-4 bg-blue-50 rounded-xl">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
            <div>
              <p className="text-sm font-medium text-gray-600">
                Initial corpus
              </p>
              <p className="text-lg font-bold" style={{ color: COLORS.accent }}>
                <LocalizedCurrency value={initialInvestment || 0} />
              </p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600">
                Total withdrawals
              </p>
              <p className="text-lg font-bold" style={{ color: COLORS.sage }}>
                <LocalizedCurrency value={results.totalWithdrawals} />
              </p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600">
                Remaining corpus
              </p>
              <p
                className="text-lg font-bold"
                style={{
                  color: results.remainingCorpus > 0 ? COLORS.sage : '#ef4444',
                }}
              >
                <LocalizedCurrency value={results.remainingCorpus} />
              </p>
            </div>
          </div>
        </div>

        <div className="mt-6 p-6 bg-gray-50 rounded-xl border border-gray-200 text-xs">
          <div className="grid md:grid-cols-2 gap-6 text-gray-700">
            {(() => {
              const definitions = [
                {
                  term: 'Period',
                  definition: 'Timeline of your withdrawal journey',
                },
                ...(stepUpEnabled && (stepUpValue || 0) > 0
                  ? [
                      {
                        term: 'Monthly withdrawal',
                        definition:
                          'Your monthly withdrawal amount for that year',
                      },
                    ]
                  : []),
                {
                  term: 'Yearly withdrawal',
                  definition: 'Total money withdrawn in that year',
                },
                {
                  term: 'Total withdrawn',
                  definition: 'All money withdrawn till that year',
                },
                {
                  term: 'Corpus at start',
                  definition: 'Your corpus value at the beginning of year',
                },
                {
                  term: 'Corpus at end',
                  definition: 'Your corpus value at the end of year',
                },
                {
                  term: 'Growth',
                  definition: 'Net growth/loss in corpus during the year',
                },
              ];

              // Split definitions into two columns
              const midPoint = Math.ceil(definitions.length / 2);
              const firstColumn = definitions.slice(0, midPoint);
              const secondColumn = definitions.slice(midPoint);

              return (
                <>
                  <div>
                    <ul className="space-y-3">
                      {firstColumn.map((item, index) => (
                        <li key={index}>
                          <strong>{item.term}:</strong> {item.definition}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <ul className="space-y-3">
                      {secondColumn.map((item, index) => (
                        <li key={index}>
                          <strong>{item.term}:</strong> {item.definition}
                        </li>
                      ))}
                    </ul>
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      </motion.div>

      <motion.div
        className="mt-16 max-w-5xl mx-auto px-6"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 1.1 }}
      >
        <article className="prose prose-lg md:prose-xl max-w-none">
          <h2 className="text-2xl md:text-4xl font-bold text-gray-900 mb-6 md:mb-8">
            What is SWP?
          </h2>

          <p className="text-lg text-gray-700 leading-relaxed mb-6 md:mb-8">
            <strong>Systematic Withdrawal Plan (SWP)</strong> is like setting up
            an automatic pension from your investments. Instead of withdrawing a
            large amount once, you withdraw a fixed amount every month - just
            like receiving a salary, but from your own investments!
          </p>

          <h3 className="text-xl md:text-2xl font-semibold text-gray-900 mt-8 md:mt-10 mb-4 md:mb-6">
            An example
          </h3>
          <p className="text-lg text-gray-700 leading-relaxed mb-6 md:mb-8">
            Think of SWP like a reverse SIP. Instead of putting ₹10,000 every
            month into investments, you take out ₹10,000 every month from your
            existing corpus. Your remaining money continues to grow while you
            enjoy regular income.
          </p>

          <h3 className="text-xl md:text-2xl font-semibold text-gray-900 mt-8 md:mt-10 mb-4 md:mb-6">
            Why SWP works for retirement
          </h3>
          <p className="text-lg text-gray-700 leading-relaxed mb-6 md:mb-8">
            When you retire, you need regular income but don't want to sell all
            investments at once. SWP gives you steady monthly income while your
            remaining corpus continues to grow, potentially lasting much longer
            than expected.
          </p>

          <h3 className="text-xl md:text-2xl font-semibold text-gray-900 mt-8 md:mt-10 mb-4 md:mb-6">
            Important terms
          </h3>
          <div className="space-y-4 md:space-y-5 mb-8 md:mb-10">
            <p className="text-lg text-gray-700">
              <strong>Corpus:</strong> Your investment pot - like your
              retirement savings account
            </p>
            <p className="text-lg text-gray-700">
              <strong>Withdrawal:</strong> Money you take out monthly - like
              pension payments
            </p>
            <p className="text-lg text-gray-700">
              <strong>Corpus depletion:</strong> How your investment pot reduces
              over time
            </p>
            <p className="text-lg text-gray-700">
              <strong>Growth rate:</strong> How much your remaining money grows
              annually
            </p>
            <p className="text-lg text-gray-700">
              <strong>Inflation-adjusted withdrawal:</strong> Increasing monthly
              withdrawal yearly to maintain purchasing power
            </p>
            <p className="text-lg text-gray-700">
              <strong>Exhaustion period:</strong> When your corpus runs out
              completely
            </p>
          </div>

          <h3 className="text-xl md:text-2xl font-semibold text-gray-900 mt-8 md:mt-10 mb-4 md:mb-6">
            SWP vs traditional retirement options
          </h3>

          <h4 className="text-lg md:text-xl font-semibold text-gray-900 mt-6 md:mt-8 mb-3 md:mb-4">
            Flexibility
          </h4>
          <p className="text-lg text-gray-700 mb-4 md:mb-6">
            <strong>SWP:</strong> Complete flexibility - can increase, decrease,
            pause, or stop withdrawals anytime based on needs.
            <br />
            <strong>Pension/Annuity:</strong> Fixed payments with little to no
            flexibility once started.
          </p>

          <h4 className="text-lg md:text-xl font-semibold text-gray-900 mt-6 md:mt-8 mb-3 md:mb-4">
            Growth potential
          </h4>
          <p className="text-lg text-gray-700 mb-4 md:mb-6">
            <strong>SWP:</strong> Your remaining corpus continues to grow with
            market returns, potentially lasting longer.
            <br />
            <strong>Fixed deposit (FD):</strong> Fixed returns with no growth
            potential, gets eroded by inflation.
          </p>

          <h4 className="text-lg md:text-xl font-semibold text-gray-900 mt-6 md:mt-8 mb-3 md:mb-4">
            Tax efficiency
          </h4>
          <p className="text-lg text-gray-700 mb-4 md:mb-6">
            <strong>SWP:</strong> Only the gains portion is taxed, making it
            more tax-efficient than interest income.
            <br />
            <strong>Interest income:</strong> Entire interest amount is taxed as
            per your tax slab.
          </p>

          <h4 className="text-lg md:text-xl font-semibold text-gray-900 mt-6 md:mt-8 mb-3 md:mb-4">
            Inheritance
          </h4>
          <p className="text-lg text-gray-700 mb-4 md:mb-6">
            <strong>SWP:</strong> Remaining corpus can be passed on to heirs.
            <br />
            <strong>Annuity:</strong> Usually ends with the investor's life,
            nothing left for heirs.
          </p>

          <h3 className="text-xl font-semibold text-gray-900 mt-8 mb-4">
            SWP vs other retirement income options
          </h3>
          <div className="overflow-x-auto mb-6">
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b-2 border-gray-200">
                  <th className="text-left py-3 px-4 font-semibold text-gray-900">
                    Income source
                  </th>
                  <th className="text-center py-3 px-4 font-semibold text-gray-900">
                    Flexibility
                  </th>
                  <th className="text-center py-3 px-4 font-semibold text-gray-900">
                    Growth potential
                  </th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-900">
                    Best for
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-gray-100">
                  <td className="py-3 px-4 font-medium text-gray-900">
                    SWP from equity mutual funds
                  </td>
                  <td className="py-3 px-4 text-center text-gray-700">High</td>
                  <td className="py-3 px-4 text-center text-gray-700">High</td>
                  <td className="py-3 px-4 text-gray-700">
                    Long-term retirement income
                  </td>
                </tr>
                <tr className="border-b border-gray-100">
                  <td className="py-3 px-4 font-medium text-gray-900">
                    Bank pension
                  </td>
                  <td className="py-3 px-4 text-center text-gray-700">Low</td>
                  <td className="py-3 px-4 text-center text-gray-700">None</td>
                  <td className="py-3 px-4 text-gray-700">
                    Guaranteed fixed income
                  </td>
                </tr>
                <tr>
                  <td className="py-3 px-4 font-medium text-gray-900">
                    Rental income
                  </td>
                  <td className="py-3 px-4 text-center text-gray-700">
                    Medium
                  </td>
                  <td className="py-3 px-4 text-center text-gray-700">
                    Medium
                  </td>
                  <td className="py-3 px-4 text-gray-700">Property owners</td>
                </tr>
              </tbody>
            </table>
          </div>

          <h3 className="text-xl font-semibold text-gray-900 mt-8 mb-4">
            Important considerations
          </h3>
          <div className="space-y-4 mb-6">
            <p className="text-lg text-gray-700">
              <strong>Market risk:</strong> Your corpus value can fluctuate with
              market conditions, affecting how long it lasts.
            </p>
            <p className="text-lg text-gray-700">
              <strong>Withdrawal rate:</strong> Higher withdrawal rates mean
              faster corpus depletion. A 4% annual withdrawal rate is often
              considered sustainable.
            </p>
            <p className="text-lg text-gray-700">
              <strong>Inflation impact:</strong> Enable inflation-adjusted
              withdrawals to maintain purchasing power over time.
            </p>
            <p className="text-lg text-gray-700">
              <strong>Emergency fund:</strong> Keep some money separate for
              emergencies, don't put everything in SWP.
            </p>
          </div>
        </article>
      </motion.div>
    </ToolLayout>
  );
};
